import React, { useState } from 'react';
import { Button, Flex, FlexItem, HelperText, HelperTextItem, TextInput } from '@patternfly/react-core';
import { ActionsColumn, type IAction, Td, Tr } from '@patternfly/react-table';
import { DECIMAL_RE, formatProductPrice } from '../../../utils/money';
import type { ProductPriceOverride } from '../../../types/models/ProductPricing';

export interface OverrideMutationResult {
  ok: boolean;
  /** Backend field-level error (data.unit_price), surfaced inline; null for non-field failures. */
  fieldError: string | null;
}

interface ClientPriceOverrideRowProps {
  override: ProductPriceOverride;
  /** The product's billing cadence — an hourly price keeps its sub-cent precision. */
  recurring: string;
  /** PUT the override; resolves with whether it succeeded + any inline field error. */
  onSave: (clientId: number, unitPrice: string) => Promise<OverrideMutationResult>;
  /** DELETE the override; the parent refetch drops this row on success. */
  onRemove: (clientId: number) => Promise<OverrideMutationResult>;
}

function ClientPriceOverrideRow({ override, recurring, onSave, onRemove }: ClientPriceOverrideRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const trimmed = draft.trim();
  const isDraftValid = DECIMAL_RE.test(trimmed);
  const helperError =
    serverError ??
    (trimmed !== '' && !isDraftValid ? 'Enter a non-negative amount with up to 4 decimal places.' : null);

  const clientLabel = override.client_name ?? `Client #${override.client_id}`;
  const inputId = `override-price-input-${override.client_id}`;

  const openEditor = () => {
    setDraft(override.unit_price);
    setServerError(null);
    setIsEditing(true);
  };

  const cancelEditor = () => {
    setIsEditing(false);
  };

  const save = async () => {
    if (!isDraftValid) {
      return;
    }
    setIsSaving(true);
    setServerError(null);
    const result = await onSave(override.client_id, trimmed);
    setIsSaving(false);
    if (result.ok) {
      setIsEditing(false);
    } else {
      setServerError(result.fieldError);
    }
  };

  const remove = async () => {
    setIsRemoving(true);
    await onRemove(override.client_id);
    // On success the parent refetch unmounts this row; on failure it stays. Clearing the flag is a
    // no-op if already unmounted.
    setIsRemoving(false);
  };

  const rowActions: IAction[] = [
    {
      title: 'Edit',
      onClick: openEditor,
      isAriaDisabled: isSaving || isRemoving,
    },
    {
      title: 'Remove',
      onClick: () => void remove(),
      isDanger: true,
      isAriaDisabled: isSaving || isRemoving,
    },
  ];

  return (
    <Tr>
      <Td dataLabel="Client">{clientLabel}</Td>
      <Td dataLabel="Price (excl. VAT)">
        {isEditing ? (
          <Flex
            spaceItems={{ default: 'spaceItemsSm' }}
            alignItems={{ default: 'alignItemsFlexStart' }}
            flexWrap={{ default: 'nowrap' }}
          >
            <FlexItem>
              <TextInput
                id={inputId}
                type="text"
                inputMode="decimal"
                value={draft}
                onChange={(_event, value) => {
                  setDraft(value);
                  if (serverError) {
                    setServerError(null);
                  }
                }}
                validated={helperError ? 'error' : 'default'}
                isDisabled={isSaving}
                aria-label={`Unit price for ${clientLabel} in euros, excluding VAT`}
              />
              {helperError && (
                <HelperText>
                  <HelperTextItem variant="error">{helperError}</HelperTextItem>
                </HelperText>
              )}
            </FlexItem>
            <FlexItem>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void save()}
                isLoading={isSaving}
                isDisabled={isSaving || !isDraftValid}
              >
                Save
              </Button>
            </FlexItem>
            <FlexItem>
              <Button variant="link" size="sm" onClick={cancelEditor} isDisabled={isSaving}>
                Cancel
              </Button>
            </FlexItem>
          </Flex>
        ) : (
          formatProductPrice(override.unit_price, recurring)
        )}
      </Td>
      <Td isActionCell>{!isEditing && <ActionsColumn items={rowActions} />}</Td>
    </Tr>
  );
}

export default ClientPriceOverrideRow;
